import React, { useState, useEffect } from 'react';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import Loader from '../UI/Loader/Loader';
import { Input, Toggle, Select, PrimaryButton, SecondaryButton } from "../UI";
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import {
    createOpenRouterWorkspace,
    updateOpenRouterWorkspace,
    getAllOrganizations,
    getAvailableOpenRouterWorkspaces,
    rotateOpenRouterWorkspaceKey,
} from '../../utils/APIs';

const isDefaultWorkspace = (w) => {
    const name = (w?.name || '').toLowerCase().trim();
    const slug = (w?.slug || '').toLowerCase().trim();
    return name === 'default' || slug === 'default' || w?.is_default === true;
};

const WorkspaceModal = ({
    setSelectedAction,
    selectedWorkspace,
    getWorkspaces,
}) => {
    const isEdit = Boolean(selectedWorkspace);
    const { userData } = useUserContext();

    const [loading, setLoading] = useState(false);
    const [rotatingKey, setRotatingKey] = useState(false);
    const [fetchingOrgs, setFetchingOrgs] = useState(false);
    const [fetchingWorkspaces, setFetchingWorkspaces] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
    const [hasReachedCap, setHasReachedCap] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Form state
    const [selectedOrgId, setSelectedOrgId] = useState(selectedWorkspace?.org_id || '');
    const [orgName, setOrgName] = useState(selectedWorkspace?.name || '');
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(selectedWorkspace?.workspace_id || '');
    const [workspaceName, setWorkspaceName] = useState(selectedWorkspace?.workspace_name || '');
    const [maskedKey, setMaskedKey] = useState(selectedWorkspace?.workspace_key || '');
    const [isActive, setIsActive] = useState(
        selectedWorkspace?.is_active !== undefined ? selectedWorkspace.is_active : true
    );

    // Fetch existing organizations and unassigned workspaces in create mode
    useEffect(() => {
        if (!isEdit) {
            const loadData = async () => {
                try {
                    setFetchingOrgs(true);
                    setFetchingWorkspaces(true);

                    const [orgsRes, wsRes] = await Promise.allSettled([
                        getAllOrganizations(userData?.token, userData?.selectedOrganization?.org_token),
                        getAvailableOpenRouterWorkspaces(userData?.token, userData?.selectedOrganization?.org_token),
                    ]);

                    if (orgsRes.status === 'fulfilled' && orgsRes.value?.status && Array.isArray(orgsRes.value.data)) {
                        setOrganizations(orgsRes.value.data);
                    }
                    if (wsRes.status === 'fulfilled' && wsRes.value?.status) {
                        const rawData = wsRes.value.data;
                        const list = Array.isArray(rawData) ? rawData : (rawData?.workspaces || []);
                        const isCap = Boolean(rawData?.has_reached_cap || (rawData?.total_remote_count >= 5));
                        setHasReachedCap(isCap);

                        const filtered = list.filter((w) => !isDefaultWorkspace(w));
                        setAvailableWorkspaces(filtered);

                        // Auto-select initial workspace: first existing unassigned workspace, or create new if none exist
                        if (!selectedWorkspaceId) {
                            if (filtered.length > 0) {
                                setSelectedWorkspaceId(filtered[0].id);
                                setWorkspaceName(filtered[0].name);
                            } else {
                                setSelectedWorkspaceId('__CREATE_NEW__');
                                setWorkspaceName(orgName || '');
                            }
                        }
                    }
                } catch (err) {
                    console.error('Failed to load initial data for workspace assignment:', err);
                } finally {
                    setFetchingOrgs(false);
                    setFetchingWorkspaces(false);
                }
            };
            loadData();
        }
    }, [isEdit, userData]);

    const handleOrgChange = (e) => {
        const orgValue = e.target.value;
        setSelectedOrgId(orgValue);
        const match = organizations.find((o) => (o.org_token || o.org_id || o.id) === orgValue);
        const name = match?.display_name || match?.name || '';
        setOrgName(name);

        // Auto-select an existing unassigned workspace if its name matches the org name
        const matchingWs = availableWorkspaces.find(
            (w) => w.name && w.name.toLowerCase() === name.toLowerCase()
        );

        if (matchingWs) {
            setSelectedWorkspaceId(matchingWs.id);
            setWorkspaceName(matchingWs.name);
        } else if (selectedWorkspaceId === '__CREATE_NEW__') {
            setWorkspaceName(name);
        } else if (!selectedWorkspaceId && availableWorkspaces.length > 0) {
            setSelectedWorkspaceId(availableWorkspaces[0].id);
            setWorkspaceName(availableWorkspaces[0].name);
        }
    };

    const handleWorkspaceChange = (e) => {
        const value = e.target.value;
        setSelectedWorkspaceId(value);

        if (value === '__CREATE_NEW__') {
            setWorkspaceName(orgName || '');
        } else if (value) {
            const matched = availableWorkspaces.find((w) => w.id === value);
            if (matched) {
                setWorkspaceName(matched.name);
            }
        } else {
            setWorkspaceName('');
        }
    };

    const handleRotateKey = async () => {
        if (!selectedWorkspace?.org_id) return;
        const confirmed = window.confirm(
            'Are you sure you want to regenerate the API key for this workspace? The previous key will be replaced.'
        );
        if (!confirmed) return;

        try {
            setRotatingKey(true);
            setSubmitError('');
            const res = await rotateOpenRouterWorkspaceKey(
                userData?.token,
                selectedWorkspace.org_id,
                userData?.selectedOrganization?.org_token
            );
            if (res?.status) {
                toast.success('OpenRouter API key regenerated successfully.');
                setMaskedKey(res.data?.workspace_key || '••••••••••••');
                await getWorkspaces();
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to regenerate API key.';
            setSubmitError(msg);
            toast.error(msg);
        } finally {
            setRotatingKey(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!selectedOrgId?.trim()) {
            setSubmitError('Please select or specify an Organization.');
            return;
        }

        if (!isEdit && !selectedWorkspaceId) {
            setSubmitError('Please select a workspace or choose to create a new one.');
            return;
        }

        try {
            setLoading(true);

            if (isEdit) {
                const payload = {
                    name: orgName.trim() || undefined,
                    is_active: isActive,
                };

                const res = await updateOpenRouterWorkspace(
                    userData?.token,
                    selectedWorkspace.org_id,
                    payload,
                    userData?.selectedOrganization?.org_token
                );

                if (res?.status) {
                    toast.success(res.message || 'OpenRouter workspace updated successfully.');
                    await getWorkspaces();
                    setSelectedAction('');
                }
            } else {
                if (selectedWorkspaceId === '__CREATE_NEW__' && !workspaceName?.trim()) {
                    setSubmitError('Please enter a Workspace Name.');
                    setLoading(false);
                    return;
                }

                const payload = {
                    org_id: selectedOrgId.trim(),
                    name: orgName.trim() || undefined,
                    ...(selectedWorkspaceId !== '__CREATE_NEW__'
                        ? { workspace_id: selectedWorkspaceId, workspace_name: workspaceName.trim() }
                        : { workspace_name: workspaceName.trim() }),
                };

                const res = await createOpenRouterWorkspace(
                    userData?.token,
                    payload,
                    userData?.selectedOrganization?.org_token
                );

                if (res?.status) {
                    toast.success(res.message || 'OpenRouter workspace assigned successfully.');
                    await getWorkspaces();
                    setSelectedAction('');
                }
            }
        } catch (error) {
            const serverMsg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to assign workspace.';
            setSubmitError(serverMsg);
            toast.error(serverMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <PrimaryModal
            title={isEdit ? 'Edit OpenRouter Workspace' : 'Assign OpenRouter Workspace'}
            hideModal={() => setSelectedAction('')}
            loading={loading || rotatingKey}
        >
            {(loading || rotatingKey) && <Loader />}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{submitError}</span>
                    </div>
                )}

                {/* Organization Selection / Display */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Organization <span className="text-red-500">*</span>
                    </label>
                    {isEdit ? (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                            <div className="font-semibold text-slate-800">{selectedWorkspace?.name}</div>
                            <div className="text-xs font-mono text-slate-500 mt-0.5">
                                Org ID: {selectedWorkspace?.org_id}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {fetchingOrgs ? (
                                <div className="text-xs text-gray-500 italic">Loading organizations...</div>
                            ) : organizations.length > 0 ? (
                                <Select
                                    value={selectedOrgId}
                                    onChange={(e) => handleOrgChange(e)}
                                    placeholder="-- Select an Organization --"
                                    options={organizations.map((org) => ({
                                        value: org.org_token || org.org_id || org.id,
                                        label: org.display_name ? `${org.display_name} (${org.name})` : (org.name || org.org_token || org.org_id || org.id),
                                    }))}
                                />
                            ) : (
                                <Input
                                    type="text"
                                    placeholder="Enter Organization ID (e.g. org_abc123)"
                                    value={selectedOrgId}
                                    onChange={(e) => setSelectedOrgId(e.target.value)}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Workspace Selection & Name */}
                {isEdit ? (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            OpenRouter Workspace Name
                        </label>
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                            <span className="font-medium text-slate-800">{selectedWorkspace?.workspace_name}</span>
                            <span className="text-xs text-slate-400 font-mono">
                                ID: {selectedWorkspace?.workspace_id ? `${selectedWorkspace.workspace_id.slice(0, 12)}...` : 'N/A'}
                            </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                            Workspace names are synchronized directly from OpenRouter and cannot be edited locally.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Dropdown for available unassigned workspaces */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                OpenRouter Workspace <span className="text-red-500">*</span>
                            </label>
                            {fetchingWorkspaces ? (
                                <div className="text-xs text-gray-500 italic">Loading available workspaces...</div>
                            ) : (
                                <Select
                                    value={selectedWorkspaceId}
                                    onChange={(e) => handleWorkspaceChange(e)}
                                    options={[
                                        ...availableWorkspaces.map((ws) => ({
                                            value: ws.id,
                                            label: `${ws.name} (${ws.slug || ws.id.slice(0, 8)})`,
                                        })),
                                        {
                                            value: '__CREATE_NEW__',
                                            label: `＋ Create New Workspace${hasReachedCap ? ' (Account Cap Reached: 5/5)' : ''}`,
                                        },
                                    ]}
                                />
                            )}
                        </div>

                        {/* State-specific UI */}
                        {selectedWorkspaceId === '__CREATE_NEW__' ? (
                            <div className="space-y-3">
                                {hasReachedCap && (
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start space-x-2">
                                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <span className="font-semibold">Workspace Limit Reached (5 of 5):</span> Your OpenRouter account is at the 5-workspace plan ceiling. You must delete an existing workspace on OpenRouter or select one of the available workspaces above to proceed.
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Input
                                        label="Workspace Name"
                                        mandatory
                                        type="text"
                                        placeholder="e.g. Acme Corp"
                                        value={workspaceName}
                                        onChange={(e) => setWorkspaceName(e.target.value)}
                                        helperText="Defaults to the organization name. A new workspace will be created on OpenRouter."
                                    />
                                </div>
                            </div>
                        ) : selectedWorkspaceId ? (
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                                <strong>Adopting existing workspace:</strong> "{workspaceName}". A fresh dedicated API key will be generated under this workspace.
                            </div>
                        ) : null}
                    </div>
                )}

                {/* API Key Section (Edit mode: display & Rotate button; Create mode: auto-minted notice) */}
                {isEdit ? (
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Workspace API Key
                        </label>
                        <div className="flex items-center space-x-2">
                            <div className="flex-1 p-2.5 bg-slate-50 border border-slate-200 rounded-lg font-mono text-xs text-slate-700 tracking-wider">
                                {maskedKey || '••••••••••••'}
                            </div>
                            <button
                                type="button"
                                disabled={rotatingKey || loading}
                                onClick={handleRotateKey}
                                className="px-3 py-2 text-xs font-semibold text-primary hover:text-primary-dark border border-primary/30 rounded-lg hover:bg-primary/5 transition disabled:opacity-50"
                            >
                                {rotatingKey ? 'Rotating...' : 'Rotate Key'}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            API keys are securely generated and managed by the server. Click "Rotate Key" to revoke and replace the current key.
                        </p>
                    </div>
                ) : (
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800 flex items-start space-x-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span>
                            No API key required from frontend. A workspace-scoped API key will automatically be minted on OpenRouter and encrypted with AES-256-GCM.
                        </span>
                    </div>
                )}

                {/* Status Toggle (Edit Mode) */}
                {isEdit && (
                    <div className="pt-1">
                        <Toggle
                            id="is_active"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            label="Active (Route AI calls through this workspace key)"
                            size="sm"
                        />
                    </div>
                )}

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-3">
                    <SecondaryButton disable={loading || rotatingKey} handleClick={() => setSelectedAction('')}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton loading={loading || rotatingKey} type="submit">
                        {isEdit ? 'Save Changes' : (selectedWorkspaceId === '__CREATE_NEW__' ? 'Create & Assign Workspace' : 'Assign Workspace')}
                    </PrimaryButton>
                </div>
            </form>
        </PrimaryModal>
    );
};

export default WorkspaceModal;
